import { useState, useMemo } from 'react';
import { COPY } from '../../data/copy';

const ITEMS = COPY.faq.items;

function StarIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="#D4AF37" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M6.5 1l1.4 3.5 3.6.3-2.7 2.4.9 3.6L6.5 9l-3.2 1.8.9-3.6L1.5 4.8l3.6-.3z" />
    </svg>
  );
}

function FAQItem({ item }: { item: typeof ITEMS[number] }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: '1px solid rgba(212,175,55,0.12)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem 0',
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          gap: '0.75rem',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {item.starred && <StarIcon />}
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-22)',
            fontWeight: 400,
            color: 'rgba(240,246,252,0.92)',
            lineHeight: 1.3,
          }}>
            {item.q}
          </span>
        </div>
        <span style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-18)',
          color: '#D4AF37',
          flexShrink: 0,
          lineHeight: 1,
          width: '1.25rem',
          textAlign: 'center',
        }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <p style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-18)',
          fontWeight: 300,
          color: 'rgba(200,220,235,0.75)',
          lineHeight: 1.7,
          paddingBottom: '1.5rem',
          margin: 0,
        }}>
          {item.a}
        </p>
      )}
    </div>
  );
}

export function FAQ() {
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    if (!query.trim()) return ITEMS;
    const q = query.toLowerCase();
    return ITEMS.filter(i =>
      i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <section
      id="faq"
      style={{
        padding: 'clamp(4rem, 8vw, 7rem) 2rem',
        background: '#0A0416',
        borderTop: '1px solid rgba(212,175,55,0.1)',
      }}
    >
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase' as const,
          color: '#D4AF37',
          textAlign: 'center',
          marginBottom: '1.5rem',
        }}>
          {COPY.faq.eyebrow}
        </p>
        <h2 style={{
          textAlign: 'center',
          margin: '0 0 2rem',
          color: 'rgba(240,246,252,0.95)',
          fontFamily: 'var(--font-display)',
        }}>
          {COPY.faq.heading}
        </h2>

        <div style={{ position: 'relative', marginBottom: '2.5rem' }}>
          <svg
            width="16" height="16" viewBox="0 0 16 16" fill="none"
            aria-hidden="true"
            style={{
              position: 'absolute', left: '0.85rem', top: '50%',
              transform: 'translateY(-50%)',
              color: 'rgba(212,175,55,0.5)', pointerEvents: 'none',
            }}
          >
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={COPY.faq.searchPlaceholder}
            aria-label="Buscar pergunta"
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              border: '1px solid rgba(212,175,55,0.2)',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-15)',
              color: 'rgba(240,246,252,0.85)',
              background: 'rgba(212,175,55,0.04)',
              outline: 'none',
              transition: 'border-color var(--transition-fast)',
              boxSizing: 'border-box',
            }}
            onFocus={e => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.5)'; }}
            onBlur={e => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.2)'; }}
          />
        </div>

        {visible.length === 0 ? (
          <p style={{
            textAlign: 'center',
            color: 'rgba(200,220,235,0.4)',
            fontFamily: 'var(--font-body)',
            padding: '2rem 0',
          }}>
            Nenhuma pergunta encontrada para "{query}".
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {visible.map((item, i) => (
              <FAQItem key={i} item={item} />
            ))}
            <div style={{ borderTop: '1px solid rgba(212,175,55,0.12)' }} />
          </div>
        )}
      </div>
    </section>
  );
}
